/* David Pladen — maximum number of items per order ("DP cart drawer" →
   Order limit). Every cart request from the theme (add, change, update) goes
   through fetch, so this checks the resulting item count before the request is
   sent. Going over the maximum returns a 422 with the message, which the cart
   drawer and the product page show as their normal error. Lowering a quantity
   is always allowed. Config: window.DPCartLimit = { max, message }. */
(() => {
  const config = window.DPCartLimit;
  if (!config || !(config.max > 0) || window.fetch.dpLimited) return;

  const nativeFetch = window.fetch.bind(window);
  const message = (config.message || '').replace('[max]', config.max);

  const pathOf = (input) => {
    try {
      const url = typeof input === 'string' ? input : input.url;
      return new URL(url, window.location.origin).pathname.replace(/\.js$/, '');
    } catch (error) {
      return '';
    }
  };
  const routes = () => ({
    add: (window.routes?.cart_add_url || '/cart/add').replace(/\.js$/, ''),
    change: (window.routes?.cart_change_url || '/cart/change').replace(/\.js$/, ''),
    update: (window.routes?.cart_update_url || '/cart/update').replace(/\.js$/, ''),
  });

  /* The request body as a plain object (JSON or FormData) */
  const readBody = (body) => {
    if (!body) return {};
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (error) {
        return Object.fromEntries(new URLSearchParams(body));
      }
    }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const out = {};
      body.forEach((value, key) => {
        if (typeof value === 'string') out[key] = value;
      });
      return out;
    }
    return {};
  };

  const qty = (value, fallback = 1) => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? fallback : n;
  };

  /* Quantity of the line(s) a change/update key points at: a line key, a variant id or a 1-based line number */
  const lineQty = (cart, ref, byLine) => {
    if (byLine) return cart.items[ref - 1]?.quantity || 0;
    const key = String(ref);
    if (key.includes(':')) return cart.items.find((item) => item.key === key)?.quantity || 0;
    return cart.items.filter((item) => String(item.variant_id) === key).reduce((sum, item) => sum + item.quantity, 0);
  };

  /* Item count after the request, or null when the request does not change quantities */
  const countAfter = (kind, data, cart) => {
    const now = cart.item_count;
    if (kind === 'add') {
      const items = Array.isArray(data.items) ? data.items : data.id ? [data] : [];
      return now + items.reduce((sum, item) => sum + qty(item.quantity), 0);
    }
    if (kind === 'change') {
      if (data.quantity === undefined) return null;
      const byLine = data.line !== undefined;
      const ref = byLine ? qty(data.line, 0) : data.id;
      return now - lineQty(cart, ref, byLine) + qty(data.quantity, 0);
    }
    const updates = data.updates;
    if (!updates) return null;
    let total = now;
    if (Array.isArray(updates)) {
      updates.forEach((value, index) => {
        total += qty(value, 0) - lineQty(cart, index + 1, true);
      });
    } else {
      Object.entries(updates).forEach(([ref, value]) => {
        total += qty(value, 0) - lineQty(cart, ref, false);
      });
    }
    return total;
  };

  const blocked = () => {
    config.lastBlocked = Date.now();
    return new Response(JSON.stringify({ status: 422, message, description: message }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  };

  const limitedFetch = async (input, init = {}) => {
    const method = (init.method || (typeof input !== 'string' && input.method) || 'GET').toUpperCase();
    const path = pathOf(input);
    const r = routes();
    const kind = method !== 'POST' ? null : path === r.add ? 'add' : path === r.change ? 'change' : path === r.update ? 'update' : null;
    if (!kind) return nativeFetch(input, init);

    try {
      const data = readBody(init.body);
      const cart = await nativeFetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((res) => res.json());
      const after = countAfter(kind, data, cart);
      if (after !== null && after > config.max && after > cart.item_count) return blocked();
    } catch (error) {
      // When the check itself fails, let Shopify handle the request as usual
    }
    return nativeFetch(input, init);
  };
  limitedFetch.dpLimited = true;
  window.fetch = limitedFetch;

  /* The product page shows "description" from DPCart.add's result; the drawer
     returns null on an error, so hand the message back in that case */
  document.addEventListener('DOMContentLoaded', () => {
    const cart = window.DPCart;
    if (!cart || cart.add.dpLimited) return;
    const add = cart.add;
    cart.add = async function limitedAdd(items, trigger) {
      const started = Date.now();
      const result = await add.call(this, items, trigger);
      if (!result && config.lastBlocked >= started) return { status: 422, description: message };
      return result;
    };
    cart.add.dpLimited = true;
  });
})();
